/**
 * The Karriere page (`/careers`), built to the website draft's "3i" screen.
 *
 * Applications are a mailto to careers@coliviq.de — there is no ATS behind
 * this. A mailto fails silently for anyone without a configured mail client,
 * so the address itself stays visible wherever we ask for an application.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  INK,
  INK_DEEP,
  MUTED,
  MUTED_SOFT,
  SAND,
  LINE,
  LINE_STRONG,
  CHIP,
  EASE,
} from "@/lib/marketing-theme";
import {
  CAREERS_EMAIL,
  CAREER_VALUES,
  CAREER_BENEFITS,
  CAREER_STEPS,
  CAREER_ROLES,
  FOUNDER_QUOTE,
  type Bilingual,
  type CareerRole,
} from "@/components/careers/careers-data";

const pick = (v: Bilingual, de: boolean) => (de ? v.de : v.en);

const H2 =
  "font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]";
const H2_STYLE = { letterSpacing: "-0.04em", color: INK } as const;

function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.65, delay: delay * 0.07, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Headline reveal. The viewport trigger sits on the heading, never on the
 * individual words: a word is translated out of its clipping box, so its own
 * intersection ratio cannot reach a threshold and it would stay hidden for
 * good. The mask also carries bottom padding so descenders are not shaved.
 */
function WordReveal({
  text,
  className,
  style,
  as: Tag = "h2",
  trigger = "inView",
}: {
  text: string;
  className?: string;
  style?: React.CSSProperties;
  as?: "h1" | "h2";
  trigger?: "mount" | "inView";
}) {
  const reduce = useReducedMotion();
  const words = text.split(" ");
  const MotionTag = Tag === "h1" ? motion.h1 : motion.h2;

  if (reduce) {
    return (
      <Tag className={className} style={style}>
        {text}
      </Tag>
    );
  }

  return (
    <MotionTag
      className={className}
      style={style}
      initial="hidden"
      variants={{
        hidden: {},
        shown: { transition: { staggerChildren: 0.045 } },
      }}
      {...(trigger === "mount"
        ? { animate: "shown" }
        : {
            whileInView: "shown" as const,
            viewport: { once: true, amount: 0.2 },
          })}
    >
      {words.map((word, i) => (
        <span key={`${word}-${i}`}>
          <span className="inline-block overflow-hidden pb-[0.18em] -mb-[0.18em] align-bottom">
            <motion.span
              className="inline-block"
              variants={{ hidden: { y: "108%" }, shown: { y: "0%" } }}
              transition={{ duration: 0.75, ease: EASE }}
            >
              {word}
            </motion.span>
          </span>
          {i < words.length - 1 ? " " : null}
        </span>
      ))}
    </MotionTag>
  );
}

/** Two-column section: heading and lead on the left, a definition grid right. */
function SplitList({
  de,
  heading,
  lead,
  items,
  columns,
}: {
  de: boolean;
  heading: string;
  lead: string;
  items: { name: Bilingual; body: Bilingual }[];
  columns: 2 | 4;
}) {
  return (
    <div className="grid items-start gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
      <div className="flex flex-col gap-3">
        <WordReveal text={heading} className={H2} style={H2_STYLE} />
        <Reveal delay={1}>
          <p
            className="max-w-[46ch] text-[16px] leading-[1.6]"
            style={{ color: MUTED }}
          >
            {lead}
          </p>
        </Reveal>
      </div>
      <div
        className={`grid gap-x-12 gap-y-8 ${columns === 4 ? "sm:grid-cols-2 xl:grid-cols-4" : "sm:grid-cols-2"}`}
      >
        {items.map((item, i) => (
          <Reveal key={item.name.en} delay={i}>
            <div className="flex flex-col gap-2">
              <motion.span
                aria-hidden="true"
                className="block h-px w-full origin-left"
                style={{ background: LINE_STRONG }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true, amount: 0.8 }}
                transition={{ duration: 0.8, ease: EASE, delay: i * 0.06 }}
              />
              <span
                className="pt-6 text-[18px] font-semibold"
                style={{ letterSpacing: "-0.02em", color: INK }}
              >
                {pick(item.name, de)}
              </span>
              <span
                className="text-[15.5px] leading-[1.6]"
                style={{ color: MUTED }}
              >
                {pick(item.body, de)}
              </span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

const OPEN_ROLES_ID = "offene-stellen";
const roleDomId = (role: CareerRole) => `stelle-${role.id}`;

/**
 * Scrolls in-page rather than following the `#hash`, so the URL stays
 * `/careers`. The fixed header is cleared by the section's own top padding
 * for Offene Stellen, and by a scroll margin on each role card.
 */
function scrollToId(id: string, reduceMotion: boolean) {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
}

/**
 * One advertised role: a summary that opens into the full ad. Closed by
 * default. When it is the only role, the hero's open-roles link opens it.
 */
function RoleCard({
  role,
  de,
  open,
  onToggle,
}: {
  role: CareerRole;
  de: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const bodyId = `${roleDomId(role)}-details`;
  const subject = encodeURIComponent(pick(role.title, de));
  return (
    <div
      id={roleDomId(role)}
      className="scroll-mt-[88px] rounded-[18px] border bg-white"
      style={{ borderColor: LINE }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full flex-col gap-4 rounded-[18px] p-6 text-left sm:p-10"
      >
        <span
          className="text-[12.5px] font-semibold uppercase"
          style={{ letterSpacing: "0.08em", color: MUTED_SOFT }}
        >
          {pick(role.eyebrow, de)}
        </span>
        <span
          className="font-display text-[1.35rem] font-semibold leading-[1.2] sm:text-[1.6rem]"
          style={{ letterSpacing: "-0.03em", color: INK }}
        >
          {pick(role.title, de)}
        </span>
        <span className="flex flex-wrap gap-2">
          {role.tags.map((tag) => (
            <span
              key={tag.en}
              className="rounded-full px-3 py-1.5 text-[13.5px] font-medium"
              style={{ background: CHIP, color: INK }}
            >
              {pick(tag, de)}
            </span>
          ))}
        </span>
        <span
          className="inline-flex items-center gap-2 text-[15px] font-semibold"
          style={{ color: INK }}
        >
          {open
            ? de
              ? "Weniger anzeigen"
              : "Show less"
            : de
              ? "Stelle ansehen"
              : "View role"}
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
            strokeWidth={2.2}
          />
        </span>
      </button>

      {/* Always rendered so aria-controls resolves; hidden while closed. */}
      <motion.div
        id={bodyId}
        hidden={!open}
        initial={false}
        animate={open ? { opacity: 1, y: 0 } : { opacity: 0, y: -6 }}
        transition={{ duration: 0.4, ease: EASE }}
        // The `flex` utility would override the hidden attribute.
        className={`${open ? "flex" : "hidden"} flex-col gap-8 border-t px-6 pb-6 pt-7 sm:px-10 sm:pb-10`}
        style={{ borderColor: LINE }}
      >
        <div className="flex flex-col gap-4">
          {role.intro.map((para) => (
            <p
              key={para.en}
              className="max-w-[68ch] text-[15.5px] leading-[1.65]"
              style={{ color: MUTED }}
            >
              {pick(para, de)}
            </p>
          ))}
        </div>

        {role.sections.map((section) => (
          <div key={section.heading.en} className="flex flex-col gap-3.5">
            <h3
              className="border-t pt-5 font-display text-[18px] font-semibold"
              style={{
                letterSpacing: "-0.02em",
                color: INK,
                borderColor: LINE_STRONG,
              }}
            >
              {pick(section.heading, de)}
            </h3>
            <ul className="flex flex-col gap-2.5">
              {section.items.map((item) => (
                <li
                  key={item.en}
                  className="relative max-w-[68ch] pl-[22px] text-[15.5px] leading-[1.6] before:absolute before:left-1 before:top-[0.7em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-current"
                  style={{ color: MUTED }}
                >
                  {pick(item, de)}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="flex flex-col gap-2.5">
          <dl
            className="grid gap-px overflow-hidden rounded-[14px] border grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4"
            style={{ borderColor: LINE, background: LINE }}
          >
            {role.facts.map((fact) => (
              <div
                key={fact.label.en}
                className="flex flex-col gap-1 px-4 py-3.5"
                style={{ background: SAND }}
              >
                <dt className="text-[12.5px]" style={{ color: MUTED_SOFT }}>
                  {pick(fact.label, de)}
                </dt>
                <dd
                  className="text-[15px] font-semibold tabular-nums"
                  style={{ color: INK }}
                >
                  {pick(fact.value, de)}
                </dd>
              </div>
            ))}
          </dl>
          <span className="text-[13.5px]" style={{ color: MUTED_SOFT }}>
            {pick(role.factsNote, de)}
          </span>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={`mailto:${CAREERS_EMAIL}?subject=${subject}`}
              className="group inline-flex h-[54px] items-center gap-2.5 rounded-full px-7 text-[15.5px] font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
              style={{ background: INK }}
            >
              {de ? "Jetzt bewerben" : "Apply now"}
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5"
                strokeWidth={2.4}
              />
            </a>
          </div>
          <span className="text-[13.5px]" style={{ color: MUTED_SOFT }}>
            {pick(role.applyNote, de)}
          </span>
        </div>
      </motion.div>
    </div>
  );
}

const Careers = () => {
  const { language } = useLanguage();
  const de = language === "de";
  const reduceMotion = useReducedMotion() ?? false;
  const [openRoleId, setOpenRoleId] = useState<string | null>(null);
  const firstRole = CAREER_ROLES[0];

  return (
    <div className="min-h-screen bg-white">
      {/* ===== Hero ===== */}
      <section
        className="px-5 sm:px-8 lg:px-14"
        style={{ background: INK_DEEP, paddingTop: "var(--hero-offset)" }}
      >
        <div className="mx-auto flex max-w-[1328px] flex-col gap-9 py-20 lg:py-28">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-16">
            <WordReveal
              as="h1"
              trigger="mount"
              text={de ? "Werde Teil unseres Teams!" : "Join our team!"}
              className="max-w-[16ch] font-display text-[2.6rem] font-semibold leading-[1.02] text-white sm:text-[3.4rem] lg:text-[4rem]"
              style={{ letterSpacing: "-0.045em" }}
            />
            {/* With a role advertised, the CTA leads to Offene Stellen, where
                the role and the speculative card both sit. Without one it
                stays the speculative mailto. */}
            <motion.a
              href={firstRole ? `#${OPEN_ROLES_ID}` : `mailto:${CAREERS_EMAIL}`}
              onClick={
                firstRole
                  ? (e) => {
                      e.preventDefault();
                      scrollToId(OPEN_ROLES_ID, reduceMotion);
                    }
                  : undefined
              }
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: EASE }}
              className="group inline-flex h-[54px] w-fit shrink-0 items-center gap-2.5 rounded-full bg-white px-8 text-[16px] font-semibold transition-transform duration-300 hover:-translate-y-0.5"
              style={{ color: INK_DEEP }}
            >
              {firstRole
                ? de
                  ? "Jetzt bewerben"
                  : "Apply now"
                : de
                  ? "Initiativ bewerben"
                  : "Apply speculatively"}
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5"
                strokeWidth={2.4}
              />
            </motion.a>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
            className="max-w-[54ch] text-[17px] leading-[1.6]"
            style={{ color: "rgba(247,243,236,0.72)" }}
          >
            {de
              ? "Wir betreiben selbst WGs und bauen die Software dafür. Hilf uns, Wohnen in Städten einfacher zu machen."
              : "We run shared flats ourselves and build the software for them. Help us make living in cities simpler."}
          </motion.p>
          {firstRole && (
            <motion.a
              href={`#${OPEN_ROLES_ID}`}
              onClick={(e) => {
                e.preventDefault();
                // One role: open it and land on it. Several: land on the list.
                if (CAREER_ROLES.length === 1) {
                  setOpenRoleId(firstRole.id);
                  scrollToId(roleDomId(firstRole), reduceMotion);
                } else {
                  scrollToId(OPEN_ROLES_ID, reduceMotion);
                }
              }}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: EASE }}
              className="inline-flex w-fit items-center rounded-full border px-3.5 py-2 text-[14.5px] font-semibold transition-colors hover:bg-white/10"
              style={{
                borderColor: "rgba(247,243,236,0.28)",
                color: "rgba(247,243,236,0.92)",
              }}
            >
              {CAREER_ROLES.length === 1
                ? `${de ? "1 offene Stelle" : "1 open role"}: ${pick(firstRole.shortTitle, de)} ↓`
                : `${CAREER_ROLES.length} ${de ? "offene Stellen" : "open roles"} ↓`}
            </motion.a>
          )}
          {/* The speculative route is a mailto, which does nothing at all for
              a visitor with no mail client configured. The address is spelled
              out here so it can be read and copied by hand. */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
            className="text-[14.5px]"
            style={{ color: "rgba(247,243,236,0.6)" }}
          >
            {de ? "Oder schreib direkt an " : "Or write straight to "}
            <a
              href={`mailto:${CAREERS_EMAIL}`}
              className="underline underline-offset-4 transition-opacity hover:opacity-70"
              style={{ color: "rgba(247,243,236,0.86)" }}
            >
              {CAREERS_EMAIL}
            </a>
          </motion.p>
        </div>
      </section>

      {/* ===== Werte ===== */}
      <section
        className="px-5 py-24 sm:px-8 lg:px-14"
        style={{ background: SAND }}
      >
        <div className="mx-auto max-w-[1328px]">
          <SplitList
            de={de}
            heading={de ? "Unsere Werte." : "What we value."}
            lead={
              de
                ? "Ein kleines Team, das selbst WGs betreibt und die Software dafür baut. So arbeiten wir."
                : "A small team that runs shared flats and builds the software for them. This is how we work."
            }
            items={CAREER_VALUES}
            columns={4}
          />
        </div>
      </section>

      {/* ===== Benefits ===== */}
      <section className="bg-white px-5 py-24 sm:px-8 lg:px-14">
        <div className="mx-auto max-w-[1328px]">
          <SplitList
            de={de}
            heading={de ? "Was wir bieten." : "What we offer."}
            lead={
              de
                ? "Neben spannender Arbeit an einem Produkt, das echte Menschen jeden Tag nutzen."
                : "Alongside real work on a product that real people use every day."
            }
            items={CAREER_BENEFITS}
            columns={2}
          />
        </div>
      </section>

      {/* ===== Founders' quote ===== */}
      <section
        className="px-5 sm:px-8 lg:px-14"
        style={{ background: INK_DEEP }}
      >
        <Reveal className="mx-auto max-w-[1328px]">
          <div className="flex flex-col gap-7 py-20 lg:py-24">
            <p
              className="max-w-[24ch] font-display text-[2rem] font-semibold leading-[1.1] text-white sm:text-[2.6rem]"
              style={{ letterSpacing: "-0.04em" }}
            >
              {pick(FOUNDER_QUOTE.quote, de)}
            </p>
            <div className="flex flex-col gap-0.5">
              <span className="text-[15.5px] font-semibold text-white">
                {FOUNDER_QUOTE.names}
              </span>
              <span
                className="text-[14.5px]"
                style={{ color: "rgba(247,243,236,0.6)" }}
              >
                {pick(FOUNDER_QUOTE.role, de)}
              </span>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ===== So bewirbst du dich ===== */}
      <section className="bg-white px-5 py-24 sm:px-8 lg:px-14">
        <div className="mx-auto flex max-w-[1328px] flex-col items-center gap-12">
          <WordReveal
            text={de ? "So bewirbst du dich." : "How to apply."}
            className={`${H2} text-center`}
            style={H2_STYLE}
          />
          <div className="grid w-full gap-10 sm:grid-cols-3">
            {CAREER_STEPS.map((step, i) => (
              <Reveal key={step.no} delay={i}>
                <div className="flex flex-col items-center gap-3 text-center">
                  <motion.span
                    initial={{ scale: 0.6, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true, amount: 0.6 }}
                    transition={{
                      type: "spring",
                      stiffness: 340,
                      damping: 22,
                      delay: 0.1 + i * 0.06,
                    }}
                    className="flex h-14 w-14 items-center justify-center rounded-full text-[17px] font-semibold text-white"
                    style={{ background: INK }}
                  >
                    {step.no}
                  </motion.span>
                  <span
                    className="mt-1 text-[17px] font-semibold"
                    style={{ letterSpacing: "-0.02em", color: INK }}
                  >
                    {pick(step.name, de)}
                  </span>
                  <span
                    className="max-w-[34ch] text-[15px] leading-[1.6]"
                    style={{ color: MUTED }}
                  >
                    {pick(step.body, de)}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Offene Stellen ===== */}
      <section
        id={OPEN_ROLES_ID}
        className="px-5 py-24 sm:px-8 lg:px-14"
        style={{ background: SAND }}
      >
        <div
          className={`mx-auto grid max-w-[1328px] gap-10 lg:gap-16 ${firstRole ? "items-start lg:grid-cols-[0.8fr_1.2fr]" : "items-center lg:grid-cols-2"}`}
        >
          <div className="flex min-w-0 flex-col gap-3">
            <WordReveal
              text={de ? "Offene Stellen." : "Open roles."}
              className={H2}
              style={H2_STYLE}
            />
            <Reveal delay={1}>
              <p
                className="max-w-[44ch] text-[16px] leading-[1.6]"
                style={{ color: MUTED }}
              >
                {firstRole
                  ? CAREER_ROLES.length === 1
                    ? de
                      ? "Eine Stelle ist gerade ausgeschrieben. Nichts dabei? Schreib uns trotzdem."
                      : "One role is open right now. Nothing that fits? Write to us anyway."
                    : de
                      ? `${CAREER_ROLES.length} Stellen sind gerade ausgeschrieben. Nichts dabei? Schreib uns trotzdem.`
                      : `${CAREER_ROLES.length} roles are open right now. Nothing that fits? Write to us anyway.`
                  : de
                    ? "Gerade ist nichts ausgeschrieben. Wir suchen trotzdem immer Menschen, die mit anpacken wollen."
                    : "Nothing is advertised right now. We are always looking for people who want to pitch in, all the same."}
              </p>
            </Reveal>
          </div>
          <div className="flex min-w-0 flex-col gap-6">
            {CAREER_ROLES.map((role) => (
              <Reveal key={role.id} delay={1}>
                <RoleCard
                  role={role}
                  de={de}
                  open={openRoleId === role.id}
                  onToggle={() =>
                    setOpenRoleId((cur) => (cur === role.id ? null : role.id))
                  }
                />
              </Reveal>
            ))}
            <Reveal delay={1}>
              <div
                className="flex flex-col gap-6 rounded-[18px] border bg-white p-8 sm:p-10"
                style={{ borderColor: LINE }}
              >
                <span
                  className="text-[19px] font-semibold"
                  style={{ letterSpacing: "-0.02em", color: INK }}
                >
                  {de
                    ? "Kein passender Job dabei? Schreib uns einfach."
                    : "Nothing that fits? Write to us anyway."}
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href={`mailto:${CAREERS_EMAIL}`}
                    className="group inline-flex h-[54px] items-center gap-2.5 rounded-full px-7 text-[15.5px] font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                    style={{ background: INK }}
                  >
                    {CAREERS_EMAIL}
                    <ArrowRight
                      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5"
                      strokeWidth={2.4}
                    />
                  </a>
                </div>
                <span className="text-[13.5px]" style={{ color: MUTED_SOFT }}>
                  {de
                    ? "CV oder ein paar Zeilen reichen — ein Anschreiben brauchst du nicht."
                    : "A CV or a few lines is enough — no cover letter needed."}
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Link to="/contact" className="sr-only" aria-hidden="true" tabIndex={-1}>
        {de ? "Kontakt" : "Contact"}
      </Link>
    </div>
  );
};

export default Careers;
